import {
  BLUESKY_LOGIN_SCOPE,
  FACEBOOK_LOGIN_SCOPE,
  INSTAGRAM_LOGIN_SCOPE,
  LINKEDIN_LOGIN_SCOPE,
  SocialShareScope,
  THREADS_LOGIN_SCOPE,
  TIKTOK_LOGIN_SCOPE,
  YOUTUBE_LOGIN_SCOPE,
} from '@sparemin/auth';
import {
  AutomationCardsOptions,
  AutoPostOptions,
  DestinationSteps,
  DestinationStepView,
} from 'blocks/AutomationOptions/types';
import { DestinationPlatform } from 'blocks/DestinationPlatforms';
import { getPlatformInfo } from 'blocks/DestinationPlatforms/utils';
import {
  PodcastSubscription,
  SubscriptionItem,
} from 'redux/middleware/api/podcast-service';
import {
  AspectRatioName,
  AutogramClipSelectionMethod,
  AutogramMixpanelPlatform,
  AutogramSubscriptionOptions,
  AutogramVideoType,
  CompatibilityVideoType,
  DeepImmutableMap,
  VideoTypes,
} from 'types';
import { getAspectRatioName } from 'utils/aspect-ratio';
import { AUTOGRAM_DURATION_OPTIONS } from '../../constants';
import { AutomationWorkflowWizardState } from './types';

type CompatibilityTypeMap = {
  [k in AutogramVideoType]: CompatibilityVideoType;
};

const COMPATIBILITY_TYPE_MAP: CompatibilityTypeMap = {
  fullEpisode: VideoTypes.FULL_EPISODE,
  randomClip: VideoTypes.SHORT_CLIP,
};

const AUTOMATION_OPTION_MAP = {
  [AutogramClipSelectionMethod.SMART]: 'headliner',
  [AutogramClipSelectionMethod.SOUNDBITE_TAG]: 'soundbite',
};

export function getMixpanelLength(opts: AutogramSubscriptionOptions) {
  switch (opts.videoType) {
    default:
      return undefined;

    case VideoTypes.FULL_EPISODE:
      return 'long';

    case VideoTypes.RANDOM_CLIP:
      return 'short';
  }
}

export function getMixpanelPlatform(opts: AutogramSubscriptionOptions) {
  if (opts.videoType !== VideoTypes.RANDOM_CLIP) {
    return undefined;
  }

  const durationOpt = AUTOGRAM_DURATION_OPTIONS.find(
    o => o.value === opts.durationSeconds,
  );

  if (!durationOpt) return undefined;

  // removes spaces, slashes, and duration from the label.
  // e.g. "Instagram/LinkedIn: 30 sec" --> "InstagramLinkedIn"
  return durationOpt.label.replace(
    / |\/|(:.*$)/g,
    '',
  ) as AutogramMixpanelPlatform;
}

export function getCompatibilityType(
  autogramVideoType: AutogramVideoType,
): CompatibilityVideoType {
  return COMPATIBILITY_TYPE_MAP[autogramVideoType];
}

export function getScopeContextFromPlatform(
  platform: DestinationPlatform,
): SocialShareScope {
  switch (platform) {
    case 'youtube':
      return YOUTUBE_LOGIN_SCOPE;

    case 'facebook':
      return FACEBOOK_LOGIN_SCOPE;

    case 'instagram':
      return INSTAGRAM_LOGIN_SCOPE;

    case 'linkedin':
      return LINKEDIN_LOGIN_SCOPE;

    case 'threads':
      return THREADS_LOGIN_SCOPE;

    case 'bluesky':
      return BLUESKY_LOGIN_SCOPE;

    case 'tiktok':
      return TIKTOK_LOGIN_SCOPE;

    case 'twitter':
      return undefined;

    default:
      return undefined;
  }
}

export function getOptionsFromSubscription(
  subscription: any,
): AutogramSubscriptionOptions {
  const firstItem = subscription?.get('subscriptionItems')?.get(0);

  if (!firstItem) {
    return undefined;
  }

  const platform = firstItem.getIn(['autoPostVideoPreference', 'platform']);

  return {
    autopost: {
      platform,
      description: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'description',
      ]),
      googleId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'googleId',
      ]),
      madeForKids: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'madeForKids',
      ]),
      categoryId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'categoryId',
      ]),
      playlists:
        firstItem
          .getIn(['autoPostVideoPreference', 'options', 'playlists'])
          ?.toJS() ?? [],
      tags:
        firstItem
          .getIn(['autoPostVideoPreference', 'options', 'tags'])
          ?.toJS() ?? [],
      title: firstItem.getIn(['autoPostVideoPreference', 'options', 'title']),
      visibility: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        platform === 'linkedin' ? 'visibility' : 'privacyStatus',
      ]),
      facebookId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'facebookId',
      ]),
      facebookPageId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'facebookPageId',
      ]),
      tiktokUserId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'tiktokUserId',
      ]),
      tiktokPrivacyLevel: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'tiktokPrivacyLevel',
      ]),
      commentEnabled: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'commentEnabled',
      ]),
      duetEnabled: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'duetEnabled',
      ]),
      stitchEnabled: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'stitchEnabled',
      ]),
      brandContentToggle: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'brandContentToggle',
      ]),
      brandOrganicToggle: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'brandOrganicToggle',
      ]),

      assetInfo: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'assetInfo',
      ]),
      instagramMediaType: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'instagramMediaType',
      ]),
      instagramUserId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'instagramUserId',
      ]),
      linkedinUserId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'linkedinUserId',
      ]),
      linkedinAuthorId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'linkedinAuthorId',
      ]),
      twitterUserId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'twitterUserId',
      ]),
      threadsUserId: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'threadsUserId',
      ]),
      blueskyDid: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'blueskyDid',
      ]),
      allowQuotePost: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'allowQuotePost',
      ]),
      replyLevel: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'replyLevel',
      ]),
      customReplyAudiences: firstItem.getIn([
        'autoPostVideoPreference',
        'options',
        'customReplyAudiences',
      ]),
    },
    durationSeconds: firstItem.get('videoLengthSecPref'),
    frequency: firstItem.get('videoFrequencyPref'),
    isCaptionEnabled: firstItem.get('isCaptionEnabled'),
    videoType: firstItem.get('videoType'),
    clipSelectionMethod: firstItem.get('clipSelectionMethod'),
  };
}

export function getAspectRatioNameFromSubscription(
  subscription: any,
): AspectRatioName {
  const dimensions = subscription?.get('dimension');
  return getAspectRatioName(
    dimensions?.get('height'),
    dimensions?.get('width'),
  );
}

export function checkViewIsClipSelection(view: DestinationStepView): boolean {
  return view === DestinationSteps.CLIP_SELECTION;
}

export function checkViewIsAutoPosting(view: DestinationStepView): boolean {
  return view === DestinationSteps.AUTO_POSTING;
}

export function checkViewIsAutomationOptionsView(
  view: DestinationStepView,
): boolean {
  return checkViewIsClipSelection(view) || checkViewIsAutoPosting(view);
}

export function checkIsPlatformWithoutVideoType(
  newPlatform: DestinationPlatform,
): boolean {
  return ['twitter', 'linkedin', 'threads', 'bluesky'].includes(newPlatform);
}

export const getAutomationOption = (
  subscriptionItem: DeepImmutableMap<SubscriptionItem>,
): AutomationCardsOptions => {
  if (subscriptionItem.get('videoType') === VideoTypes.FULL_EPISODE) {
    return subscriptionItem.getIn(['autoPostVideoPreference', 'platform']) ===
      'youtube'
      ? AutoPostOptions.AUTO_POST
      : AutoPostOptions.EMAIL;
  }
  return (
    AUTOMATION_OPTION_MAP[subscriptionItem.get('clipSelectionMethod')] ?? null
  );
};

export const initializeDefaultPlatform = (
  defaultPlatform?: DestinationPlatform,
): AutomationWorkflowWizardState['destinationOptions'] | undefined => {
  const platformInfo = getPlatformInfo(defaultPlatform);

  return {
    aspectRatioName: platformInfo?.aspectRatio?.automation ?? 'square',
    automationOption: AutoPostOptions.AUTO_POST,
    clipDuration: undefined,
    clipSelectionMethod: undefined,
    isDestinationOptionsComplete: false,
    platform: defaultPlatform,
    platformVideoType: platformInfo?.type ?? 'post',
    stepView: DestinationSteps.AUTO_POSTING,
    videoType: VideoTypes.RANDOM_CLIP,
  };
};

export const initializeDestinationOptions = (
  subscription?: DeepImmutableMap<PodcastSubscription>,
  defaultPlatform?: DestinationPlatform,
): AutomationWorkflowWizardState['destinationOptions'] | undefined => {
  const subscriptionItemMap = subscription?.getIn(['subscriptionItems', '0']);

  if (defaultPlatform) {
    return initializeDefaultPlatform(defaultPlatform);
  }

  if (!subscription || !subscriptionItemMap) {
    return undefined;
  }

  return {
    aspectRatioName: getAspectRatioNameFromSubscription(subscription),
    automationOption: getAutomationOption(subscriptionItemMap),
    autopostVideoToYouTube:
      subscriptionItemMap.getIn(['autoPostVideoPreference', 'platform']) ===
      'youtube',
    clipDuration: subscriptionItemMap.get('videoLengthSecPref'),
    clipSelectionMethod: subscriptionItemMap.get('clipSelectionMethod'),
    isDestinationOptionsComplete: true,
    // There is no way for obtaining this information when loading an existing automation
    // as the information is not stored at the BE.
    platform: undefined,
    platformVideoType: undefined,
    stepView: DestinationSteps.CLIP_SELECTION,
    videoType: subscriptionItemMap.get('videoType'),
  };
};
